import "./style.scss";
import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import VerticalLine from "@/src/components/VerticalLine/VerticalLine";
import { RootState } from "@/src/store";
import { setFilterStatus } from "@/src/store/slices/OfficeAccountingPage/officeAccountingFiltersSlice";
import {
  hideAllColumns,
  showAllColumns,
  toggleColumnVisibility,
} from "@/src/store/slices/OfficeAccountingPage/officeAccountingTableSlice";
import { setActualUserConfig } from "@/src/store/slices/UserOptions/userOptionsSlice";
import {
  normalizeVisibleOAPColumns,
  officeAccountingAllFieldsList,
  officeAccountingFilterStatusesConst,
} from "@/src/constants/officeAccountingTableConfig";
import { applicationPageSvgConst } from "@/src/svg/applicationsPage/applicationsPageSvgExport";
import CustomDescriptionPopUP from "@/src/components/CustomUtils/CustomDescriptionPopUP/CustomDescriptionPopUP";

interface OAPHeaderProps {
  loadActualForm: () => void;
  isShowSettingsModal: boolean;
  setIsShowSettingsModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const OAPHeader: FC<OAPHeaderProps> = ({ loadActualForm, isShowSettingsModal, setIsShowSettingsModal }) => {
  const dispatch = useDispatch();
  const activeStatus = useSelector((state: RootState) => state.officeAccountingFilters.filterStatus);
  const visibleColumns = useSelector((state: RootState) => state.officeAccountingTable.visibleColumns);
  const oapFixed = useSelector((state: RootState) => state.userOptions.OAPConfig.OAPFixed);

  const handleSettingsModal = () => {
    setIsShowSettingsModal((prev) => !prev);
    if (isShowSettingsModal) {
      dispatch(
        setActualUserConfig({
          OAPConfig: {
            OAPTable: normalizeVisibleOAPColumns(visibleColumns),
            OAPFixed: oapFixed ?? null,
          },
        }),
      );
    }
  };

  return (
    <div className="office-accounting-page-header">
      <div className="office-accounting-page-header-body">
        <div className="office-accounting-page-header-tabs">
          {officeAccountingFilterStatusesConst.map((status) => (
            <div key={status} className="office-accounting-page-header-tab" onClick={() => dispatch(setFilterStatus(status))}>
              <BtnDefault reverse={activeStatus === status}>
                <p>{status}</p>
              </BtnDefault>
            </div>
          ))}
        </div>
        <div className="header-actions">
          <CustomDescriptionPopUP text="Reload accounting records and filter options from the server.">
            <BtnDefault onClick={loadActualForm}>
              <p>Refresh</p>
            </BtnDefault>
          </CustomDescriptionPopUP>
          <VerticalLine />
          <CustomDescriptionPopUP text="Open table settings and save the current column configuration.">
            <BtnDefault onlyIcon={true} onClick={handleSettingsModal}>
              <applicationPageSvgConst.SettingsSvg />
            </BtnDefault>
          </CustomDescriptionPopUP>
        </div>
      </div>
      <div className={`office-accounting-page-header-settings ${isShowSettingsModal ? "active" : ""}`}>
        <div className="office-accounting-page-header-settings-ul">
          {officeAccountingAllFieldsList.map((col) => (
            <div className="office-accounting-page-header-settings-li" key={col.id}>
              <label>
                <input
                  type="checkbox"
                  checked={visibleColumns.some((c) => c.id === col.id)}
                  onChange={() => dispatch(toggleColumnVisibility(col.id))}
                />
                <div className="office-accounting-page-header-settings-li-tab">
                  <applicationPageSvgConst.SettingsTabPlusSvg />
                  <p>{col.title}</p>
                  <applicationPageSvgConst.SettingsTabDeleteSvg />
                </div>
              </label>
            </div>
          ))}
        </div>
        <div className="office-accounting-page-header-settings-buttons">
          <div className="office-accounting-page-header-settings-buttons-both">
            <BtnDefault onClick={() => dispatch(showAllColumns())}>
              <p>Choose all</p>
              <applicationPageSvgConst.ChooseAllSvg />
            </BtnDefault>
            <BtnDefault onClick={() => dispatch(hideAllColumns())}>
              <p>Clear all</p>
              <applicationPageSvgConst.ClearAllSvg />
            </BtnDefault>
          </div>
          <VerticalLine />
          <div className="office-accounting-page-header-settings-buttons-save">
            <BtnDefault reverse={true} onClick={handleSettingsModal}>
              <p>Save</p>
              <applicationPageSvgConst.SaveSvg />
            </BtnDefault>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAPHeader;
